/**
 * ApiKeyFormPageV2 — redesigned create/edit form for personal-access
 * tokens. Sections:
 *   01 Basic — name + description
 *   02 Scopes — toggle-chip list of available scopes (with sub-line
 *              description per scope). Empty = wildcard "all access".
 *   03 Expiration — Select (Never / 30 / 60 / 90 days / 6 mo / 1 yr).
 *
 * On create, the API returns a `plain_text_token` once — we open a
 * Modal showing it with a copy-to-clipboard control before bouncing
 * back to the list.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApiKeys } from '../../../../hooks/useApiKeys';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { Button, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
} from '../../../redesign/edit';
import TokenSecretModal from './TokenSecretModal';
import { formatDate } from './ApiKeysPageV2';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const EXPIRATION_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '30',    label: '30 days' },
  { value: '60',    label: '60 days' },
  { value: '90',    label: '90 days' },
  { value: '180',   label: '6 months' },
  { value: '365',   label: '1 year' },
];

function computeExpiresAt(option) {
  if (option === 'never') return null;
  const date = new Date();
  date.setDate(date.getDate() + parseInt(option, 10));
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

/* ─── Scope toggle chip ───────────────────────────────────────── */

function ScopeChip({ scope, description, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-start text-left transition-all duration-150',
      )}
      style={{
        gap: 10,
        padding: '10px 14px',
        // Theme-aware fills so the chip flips with dark mode. The
        // label uses text-navy (which flips light in dark); a
        // hardcoded light card left near-white text on light cream,
        // i.e. invisible scope names in dark mode.
        background: selected ? 'var(--color-teal-green-soft)' : 'rgb(var(--color-cream))',
        border: `1px solid ${selected ? 'rgb(var(--color-teal-green))' : 'rgb(var(--color-sand))'}`,
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
        width: '100%',
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
        e.currentTarget.style.background = 'rgb(var(--color-surface))';
      }}
      onMouseLeave={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
        e.currentTarget.style.background = 'rgb(var(--color-cream))';
      }}
    >
      <span
        aria-hidden
        className="grid place-items-center shrink-0"
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          marginTop: 2,
          background: selected ? 'rgb(var(--color-teal-green))' : 'transparent',
          border: `1.5px solid ${selected ? 'rgb(var(--color-teal-green))' : 'rgb(var(--color-sand))'}`,
          color: '#fff',
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {selected && '✓'}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className="font-mono text-navy block truncate"
          style={{ fontSize: 12.5, fontWeight: 600 }}
        >
          {scope}
        </span>
        {description && (
          <span
            className="text-text-muted block"
            style={{ fontSize: 11.5, marginTop: 2, fontFamily: 'var(--font-sans)' }}
          >
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

/* ─── Main ────────────────────────────────────────────────────── */

export default function ApiKeyFormPageV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const {
    loading,
    fetchOne,
    create,
    update,
    availableScopes,
    fetchAvailableScopes,
  } = useApiKeys();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    abilities: [],
    expiration: 'never',
  });
  // Saved expiry, shown read-only on edit.
  const [currentExpiresAt, setCurrentExpiresAt] = useState(null);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [plainTextToken, setPlainTextToken] = useState('');

  useEffect(() => {
    fetchAvailableScopes();
    if (isEdit) {
      (async () => {
        const data = await fetchOne(id);
        if (data) {
          setFormData({
            name: data.name || '',
            description: data.description || '',
            abilities: Array.isArray(data.abilities)
              ? data.abilities.filter((a) => a !== '*')
              : [],
            // Expiration only applies when issuing a new token. On edit
            // the payload omits expires_at, so the saved expiry is kept;
            // this value is never sent.
            expiration: 'never',
          });
          setCurrentExpiresAt(data.expires_at || null);
        }
      })();
    }
    // Hooks are stable useCallbacks; safe to omit.
  }, [id, isEdit, fetchAvailableScopes, fetchOne]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleScope = (scope) => {
    setFormData((prev) => ({
      ...prev,
      abilities: prev.abilities.includes(scope)
        ? prev.abilities.filter((s) => s !== scope)
        : [...prev.abilities, scope],
    }));
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const payload = {
      name: formData.name,
      description: formData.description,
      abilities: formData.abilities.length > 0 ? formData.abilities : null,
    };
    // Edit omits expires_at: APIv2 keeps the existing expiry when the
    // key is absent, while null would remove it.
    if (!isEdit) payload.expires_at = computeExpiresAt(formData.expiration);
    if (isEdit) {
      const result = await update(id, payload);
      if (result) navigate('/api-keys');
    } else {
      const result = await create(payload);
      if (result) {
        setPlainTextToken(result.plain_text_token);
        setTokenModalOpen(true);
      }
    }
  };

  const handleTokenModalClose = () => {
    setTokenModalOpen(false);
    setPlainTextToken('');
    navigate('/api-keys');
  };

  const eyebrow = isEdit ? 'Editing API key' : 'New API key';
  const title = isEdit ? formData.name || `API key #${id}` : 'New API key';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !formData.name && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>token_id · <strong className="text-navy">{id}</strong></>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/api-keys" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="api-key-form"
              leadingIcon={SaveIconSvg}
              disabled={loading}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <form
          id="api-key-form"
          onSubmit={handleSave}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0, maxWidth: 960 }}
        >
          <FormSection title="Basic" num="01">
            <FieldBlock label="Name" required htmlFor="f-key-name">
              <TextInput
                id="f-key-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={255}
              />
            </FieldBlock>
            <FieldBlock
              label="Description"
              required
              hint="What is this key used for?"
              htmlFor="f-key-description"
              noMargin
            >
              <Textarea
                id="f-key-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                required
                maxLength={1000}
              />
            </FieldBlock>
          </FormSection>

          <FormSection
            title="Scopes"
            num="02"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {formData.abilities.length > 0
                  ? `${formData.abilities.length} selected`
                  : 'Leave empty for full access'}
              </span>
            }
          >
            {availableScopes.length === 0 ? (
              <div
                className="bg-surface border border-sand text-text-muted italic text-center"
                style={{ borderRadius: 8, padding: '12px 14px', fontSize: 13 }}
              >
                Loading scopes…
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2"
                style={{ gap: 8 }}
              >
                {availableScopes.map((s) => (
                  <ScopeChip
                    key={s.scope}
                    scope={s.scope}
                    description={s.description}
                    selected={formData.abilities.includes(s.scope)}
                    onToggle={() => toggleScope(s.scope)}
                  />
                ))}
              </div>
            )}
          </FormSection>

          <FormSection title="Expiration" num="03">
            <FieldRow>
              <FieldBlock
                label="Expires after"
                hint={
                  isEdit
                    ? 'Editing keeps the key\'s current expiry.'
                    : 'When the token stops being accepted.'
                }
                htmlFor="f-key-expiration"
                noMargin
              >
                {isEdit ? (
                  <TextInput
                    id="f-key-expiration"
                    value={formatDate(currentExpiresAt)}
                    readOnly
                  />
                ) : (
                  <Select
                    id="f-key-expiration"
                    name="expiration"
                    value={formData.expiration}
                    onChange={handleChange}
                  >
                    {EXPIRATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                )}
              </FieldBlock>
              <div />
            </FieldRow>
          </FormSection>
        </form>
      </div>

      <TokenSecretModal
        open={tokenModalOpen}
        plainTextToken={plainTextToken}
        onClose={handleTokenModalClose}
      />
    </div>
  );
}
